import { proxy } from 'valtio';
import { proxyMap } from 'valtio/utils';
import { getMembers, type MembersResponse } from '~/api/organizations';
import rootStore from '~/store';

class OrgMembersStore {
  isLoading: boolean;
  isRefreshing: boolean;
  members: MembersResponse;
  selectedExternalMembers: Map<string, string> = proxyMap<string, string>();

  constructor() {
    this.isLoading = false;
    this.isRefreshing = false;
    this.members = {
      orgMembers: [],
      guestMembers: [],
      pendingMembers: [],
      cgMembers: [],
    };
    this.selectedExternalMembers = proxyMap<string, string>();
  }

  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  async loadMembers() {
    if (!rootStore.currentOrganizationDetail.isLoading) {
      const members = await getMembers(rootStore.currentOrganizationDetail.id);
      this.members = members;
    }
  }

  async resetSelections(showLoading = true) {
    // Don't show loading indicator for background refreshes
    if (showLoading) {
      this.setLoading(true);
    } else {
      this.isRefreshing = true;
    }

    try {
      this.loadMembers();
      this.selectedExternalMembers.clear();
    } finally {
      if (showLoading) {
        this.setLoading(false);
      } else {
        this.isRefreshing = false;
      }
    }
  }

  get selectedExternalMembersArray() {
    return Array.from(this.selectedExternalMembers.entries()).map(([email, role]) => ({
      email,
      role,
    }));
  }

  addExternalMember(email: string, role: string = '') {
    if (!this.selectedExternalMembers.has(email)) {
      this.selectedExternalMembers.set(email, role);
    }
  }

  toggleExternalMember(email: string, role: string = '') {
    const isSelected = this.selectedExternalMembers.has(email);

    if (isSelected) {
      this.selectedExternalMembers.delete(email);
    } else {
      this.selectedExternalMembers.set(email, role);
    }
  }

  updateMemberRole(email: string, role: string) {
    if (this.selectedExternalMembers.has(email)) {
      this.selectedExternalMembers.set(email, role);
    }
  }

  updateAllMembersRole(role: string) {
    this.selectedExternalMembers.keys().forEach((email) => {
      this.updateMemberRole(email, role);
    });
  }
}

const orgMembersStore = proxy(new OrgMembersStore());
export default orgMembersStore;
